/* Machines — the register of every machine in the selection, each row
 * opening in place into that machine's shift-by-shift month. */

import { stageNoun } from '../../lib/api_machines'

import { Fragment, useEffect, useRef, useState } from 'react'
import { Blueprint, PanelHead } from '../../components/ui'
import { EffAxis, effY } from '../../components/charts/MachineDayLines'
import { SHIFTS } from '../../lib/api_machines'
import type { MachineExtreme, MachineRow, Shift } from '../../lib/api_machines'
import { num } from '../../lib/format'
import {
  BAND, bandFill, dayNum, Meter, PatternTag, R, Spark, story, unitName,
} from './parts'
import type { Ctx } from './parts'

export type Sort = 'lost' | 'mean' | 'sd' | 'spread' | 'name'

const SORTS: { k: Sort; label: string }[] = [
  { k: 'lost', label: 'Lost kg' }, { k: 'mean', label: 'Lowest eff' },
  { k: 'sd', label: 'Most erratic' }, { k: 'spread', label: 'Shift gap' },
  { k: 'name', label: 'Machine' },
]
const COLUMNS = 12
/* The shifts in a machine's own chart take the bar ramp, as the design has
   it, not the shift colours of the Shifts tab — here they sit side by side
   on one machine and only have to be told apart. */
const DETAIL_COLOR = [BAND.ok, BAND.mid, BAND.warn]
const DETAIL_H = 220

/** An element's width, kept current — the open machine's panel is pinned
 *  to the visible width of the table rather than to its scrolled width. */
function useWidth() {
  const ref = useRef<HTMLDivElement>(null)
  const [width, setWidth] = useState<number | null>(null)
  useEffect(() => {
    const el = ref.current
    if (!el) return
    const measure = () => setWidth(el.clientWidth)
    measure()
    if (typeof ResizeObserver === 'undefined') return
    const obs = new ResizeObserver(measure)
    obs.observe(el)
    return () => obs.disconnect()
  }, [])
  return { ref, width }
}

export function RegisterTab({ c, sort, setSort, sel, close }: {
  c: Ctx
  sort: Sort
  setSort: (s: Sort) => void
  /** The machine whose row is open, if any. */
  sel: string | null
  close: () => void
}) {
  const { data } = c
  const wrap = useWidth()

  // The server sends machine order; every other sort is a view of it.
  const rows = [...data.machines]
  if (sort === 'lost') rows.sort((a, b) => b.lost - a.lost)
  if (sort === 'mean') rows.sort((a, b) => a.mean - b.mean)
  if (sort === 'sd') rows.sort((a, b) => b.sd - a.sd)
  if (sort === 'spread') rows.sort((a, b) => b.spread - a.spread)

  return (
    <Blueprint className="sheet">
      <PanelHead title="Machine register" right={
        <div className="no-print" style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
          <span className="axis-title" style={{ marginRight: 4 }}>Sort</span>
          {SORTS.map((s) => (
            <button key={s.k} type="button"
              className={`btn ${sort === s.k ? 'btn-primary' : 'btn-secondary'}`}
              style={{ padding: '5px 11px', fontSize: 12 }}
              onClick={() => setSort(s.k)}>{s.label}</button>
          ))}
        </div>
      } />
      <div ref={wrap.ref} className="scroll-x" style={{ marginTop: 16 }}>
        <table className="table">
          <thead>
            <tr>
              <th>Machine</th><th>Count</th><th style={R}>Std / shift</th><th style={R}>Days</th>
              <th>Avg day eff</th><th style={R}>Best</th><th style={R}>Worst</th><th style={R}>±</th>
              <th style={R}>Below {c.benchLabel}</th><th style={R}>Lost kg</th>
              <th>Trend</th><th>Pattern</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((m) => {
              const open = sel === m.code
              return (
                <Fragment key={m.code}>
                  <tr id={`mc-${m.code}`} className="mcrow clickable" aria-expanded={open}
                    onClick={() => c.open(m.code)}
                    style={open ? { background: 'var(--color-accent-100)' } : undefined}>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      <span style={{
                        display: 'inline-block', width: 14, fontSize: 10,
                        color: 'var(--color-neutral-600)',
                      }}>{open ? '▾' : '▸'}</span>
                      {m.name}
                      <div style={{ fontSize: 11, color: 'var(--color-neutral-600)' }}>
                        {unitName(m.unit)}{m.is_seg ? ' · count changed' : ''}
                      </div>
                    </td>
                    {/* A machine that changed count reads as the sequence
                        ('20 KW → 30 KW'); a segment as its count and days. */}
                    <td style={{ fontSize: 12, color: 'var(--color-neutral-700)' }}>
                      {m.is_seg ? `${m.count} (${m.range})` : m.count || '–'}
                    </td>
                    <td className="num" style={R}>{num(m.std)}</td>
                    <td className="num" style={R}>{m.n}</td>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <Meter width={70} value={m.mean} max={120} fill={bandFill(m.mean, c.bench)} />
                        <span className="num">{num(m.mean, 1)}%</span>
                      </div>
                    </td>
                    <td className="num" style={DIM}>{num(m.best.eff)}%</td>
                    <td className="num" style={DIM}>{num(m.worst.eff)}%</td>
                    <td className="num" style={DIM}>{num(m.sd, 1)}</td>
                    <td className="num" style={R}>{m.below}/{m.n}</td>
                    <td className="num" style={R}>{num(m.lost)}</td>
                    <td>
                      <div style={{ width: 100 }}>
                        <Spark m={m} dates={data.dates} bench={c.bench} height={22} pixels={22}
                          stroke={1.4} />
                      </div>
                    </td>
                    <td><PatternTag pattern={m.pattern} /></td>
                  </tr>
                  {open ? (
                    <tr>
                      <td colSpan={COLUMNS} style={{ padding: 0, background: 'var(--color-bg)' }}>
                        <div style={{
                          position: 'sticky', left: 0, boxSizing: 'border-box',
                          width: wrap.width ?? '100%', padding: '18px 20px 22px',
                          borderLeft: '3px solid var(--color-accent-500)',
                          borderBottom: '1px solid var(--color-divider)',
                        }}>
                          <Detail m={m} c={c} close={close} />
                        </div>
                      </td>
                    </tr>
                  ) : null}
                </Fragment>
              )
            })}
          </tbody>
        </table>
      </div>
      <div className="pretty" style={{ fontSize: 12, color: 'var(--color-neutral-600)', marginTop: 14 }}>
        Click a machine to expand its daily shift-by-shift record; click again to collapse.
        {' '}{footer(c)}
      </div>
    </Blueprint>
  )
}

const DIM: React.CSSProperties = { textAlign: 'right', color: 'var(--color-neutral-700)' }

/** How efficiency is measured, and what was left out of it. */
function footer(c: Ctx): string {
  const t = c.data.totals
  const left = t.implausible + t.over + t.no_standard
  const parts = [
    c.data.basis === 'sitra'
      ? `Efficiency is actual output against the SITRA norm for the count — its grams per spindle per shift times the spindles the frame is built with — on the ${stageNoun(c.data.norm_stages)}, and against the sheet’s own standard on every other stage.`
      : 'Efficiency is actual output against the sheet’s own standard for the count being run.',
  ]
  if (left) {
    const why = [
      t.implausible ? `${num(t.implausible)} with a standard more than three times the output` : '',
      t.over ? `${num(t.over)} with output over ${num(c.data.over_standard)}% of standard` : '',
      t.no_standard ? `${num(t.no_standard)} with no standard entered` : '',
    ].filter(Boolean)
    parts.push(
      `Of ${num(t.rows)} machine-shifts on the sheets, ${num(left)} were set aside as data errors — `
      + `${why.join(', ')} — and their ${num(t.unrated_kg)} kg are in no efficiency or loss figure here.`)
  }
  if (t.no_output) {
    parts.push(`${num(t.no_output)} machine-shifts had no output and carry no target.`)
  }
  const std = t.untracked.filter((u) => u.why === 'standard')
  const thin = t.untracked.filter((u) => u.why === 'days')
  if (std.length) {
    parts.push(`${names(std)} ${std.length === 1 ? 'is' : 'are'} not read: the standard is unusable on most of the shifts ${std.length === 1 ? 'it' : 'they'} ran.`)
  }
  if (thin.length) {
    parts.push(`${names(thin)} ${thin.length === 1 ? 'has' : 'have'} fewer than ${c.data.min_days} rated days and ${thin.length === 1 ? 'is' : 'are'} not read.`)
  }
  return parts.join(' ')
}

/** Named while the list is short enough to read, counted once it is not. */
function names(list: { name: string }[]): string {
  if (list.length > 8) return `${list.length} machines`
  return list.map((u) => u.name).join(', ')
}

// ------------------------------------------------------------------ detail
function Detail({ m, c, close }: { m: MachineRow; c: Ctx; close: () => void }) {
  const { data } = c
  const n = data.dates.length
  const slot = n ? 1000 / n : 30
  const bar = Math.max(2, (slot - 4) / 3)
  const at = new Map(data.dates.map((d, i) => [d, i]))
  const on = (iso: string) => `${dayNum(iso)} ${c.monShort}`
  // The shift bar under the pointer: a readout opens under the chart with
  // that shift alone — its kilograms, utilisation and efficiency, who had
  // it, and the stoppages the register books on it, each as a share of
  // the shift's minutes.
  const [hov, setHov] = useState<{ i: number; s: Shift } | null>(null)
  const readout = hov && hov.i < n ? shiftReadout(m, c, hov.i, hov.s) : null

  /** Who had the machine on the shift that made the day. */
  const who = (x: MachineExtreme) =>
    x.shift ? `Shift ${x.shift} · ${x.supervisor ?? 'supervisor not entered'}` : 'Supervisor –'

  const stats: { label: string; value: string; sub?: string }[] = [
    { label: 'Avg day eff', value: `${num(m.mean, 1)}%` },
    { label: 'Spread', value: `±${num(m.sd, 1)}` },
    { label: 'Best day', value: `${num(m.best.eff)}% · ${on(m.best.date)}`, sub: who(m.best) },
    { label: 'Worst day', value: `${num(m.worst.eff)}% · ${on(m.worst.date)}`, sub: who(m.worst) },
    { label: `Below ${c.benchLabel}`, value: `${m.below} of ${m.n} days` },
    { label: 'Lost vs standard', value: `${num(m.lost)} kg` },
  ]
  // Not in the design, whose register was per unit: the tracker books
  // stoppages against the machine, so its own total belongs with its record.
  if (data.register) {
    stats.push({
      label: 'Stoppage booked', value: `${num(m.stop_min)} min`,
      sub: m.stops.length
        ? `mostly ${m.stops[0].reason} · ${num(m.stops[0].min)} min`
        : 'nothing on the register',
    })
  }

  return (
    <>
      <div style={{
        display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
        gap: 16, flexWrap: 'wrap',
      }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <h2 style={{ fontSize: 20, margin: 0 }}>
              {m.full_name}{m.is_seg ? ` · ${m.range}` : ''}
            </h2>
            <PatternTag pattern={m.pattern} />
          </div>
          <div className="pretty" style={{
            fontSize: 13, color: 'var(--color-neutral-700)', marginTop: 6,
            maxWidth: '80ch', lineHeight: 1.5,
          }}>{story(m, c)}</div>
        </div>
        <button className="btn btn-ghost no-print" type="button" onClick={close}>Collapse ▴</button>
      </div>

      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(130px,1fr))',
        gap: 12, marginTop: 18,
      }}>
        {stats.map((s) => (
          <div key={s.label} style={{ borderTop: '1px solid var(--color-divider)', paddingTop: 8 }}>
            <div className="axis-title">{s.label}</div>
            <div className="num" style={{
              fontFamily: 'var(--font-heading)', fontSize: 22, marginTop: 3,
            }}>{s.value}</div>
            {s.sub ? (
              <div style={{ fontSize: 11, color: 'var(--color-neutral-600)', marginTop: 3 }}>{s.sub}</div>
            ) : null}
          </div>
        ))}
      </div>

      <div style={{ ...FRAME, marginTop: 22 }}>
        <EffAxis height={DETAIL_H} />
        <div onMouseLeave={() => setHov(null)} style={{ position: 'relative' }}>
        <svg viewBox={`0 0 1000 ${DETAIL_H}`} preserveAspectRatio="none" style={{
          width: '100%', height: DETAIL_H, display: 'block',
          borderLeft: '1px solid var(--color-neutral-500)',
          borderBottom: '1px solid var(--color-neutral-500)',
        }}>
          {data.dates.map((d, i) => (
            <rect key={d} x={i * slot} y={0} width={slot} height={DETAIL_H}
              fill={hov?.i === i ? 'var(--c-grid)' : 'transparent'} />
          ))}
          {[40, 60, 80, 100, 120].map((t) => (
            <line key={t} x1="0" y1={effY(t, DETAIL_H)} x2="1000" y2={effY(t, DETAIL_H)}
              stroke="var(--c-grid)" strokeWidth="1" vectorEffect="non-scaling-stroke" />
          ))}
          <line x1="0" y1={effY(c.bench, DETAIL_H)} x2="1000" y2={effY(c.bench, DETAIL_H)}
            stroke="var(--c-ink)" strokeWidth="1.4" strokeDasharray="6 4"
            vectorEffect="non-scaling-stroke" />
          {m.days.flatMap((d) => SHIFTS.map((s, si) => {
            const v = d[s]
            if (v === null || v <= 0) return null
            const y = effY(v, DETAIL_H)
            return (
              <rect key={d.date + s} x={(at.get(d.date) ?? 0) * slot + 2 + si * bar} y={y}
                width={bar} height={Math.max(1, DETAIL_H - y)} fill={DETAIL_COLOR[si]}
                stroke={hov && hov.i === at.get(d.date) && hov.s === s ? 'var(--c-ink)' : 'none'}
                strokeWidth={1.5} vectorEffect="non-scaling-stroke" style={{ pointerEvents: 'none' }} />
            )
          }))}
          {/* One hit area per shift slot, the full height, so a short bar
              is as easy to reach as a tall one. */}
          {data.dates.flatMap((d, i) => SHIFTS.map((s, si) => (
            <rect key={d + s} x={i * slot + 2 + si * bar} y={0} width={bar} height={DETAIL_H}
              fill="transparent"
              onMouseEnter={() => setHov({ i, s })} onClick={() => setHov({ i, s })}
              onTouchStart={() => setHov({ i, s })} />
          )))}
        </svg>
        {readout ? (
          <div style={{
            position: 'absolute', top: 'calc(100% + 26px)', left: readout.left, transform: readout.shift,
            pointerEvents: 'none', width: 300, padding: '12px 14px', background: 'var(--color-bg)',
            color: 'var(--color-text)', border: '1px solid var(--color-divider)',
            boxShadow: 'var(--shadow-md)', fontSize: 12, zIndex: 4,
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, fontWeight: 600 }}>
              <span>{readout.title}</span><span className="num">{readout.day}</span>
            </div>
            <div style={{ color: 'var(--color-neutral-700)', marginTop: 2 }}>{readout.sub}</div>
            <div style={{ color: 'var(--color-neutral-700)', marginTop: 2 }}>{readout.who}</div>
            <div className="num" style={{
              display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginTop: 8,
              paddingTop: 8, borderTop: '1px solid var(--color-divider)',
            }}>
              {readout.figures.map((f) => (
                <div key={f.label}>
                  <div style={{ fontSize: 10, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--color-neutral-600)' }}>{f.label}</div>
                  <div style={{ fontSize: 16, fontWeight: 600, marginTop: 2 }}>{f.value}</div>
                </div>
              ))}
            </div>
            <div style={{ marginTop: 10, fontSize: 10, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--color-neutral-600)' }}>
              {readout.stopHead}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 3, marginTop: 4 }}>
              {readout.stops.map((st) => (
                <div key={st.r} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span className="truncate" style={{ flex: 1, minWidth: 0, color: 'var(--color-neutral-800)' }}>{st.r}</span>
                  <div style={{ width: 54, height: 6, background: 'var(--c-track)' }}>
                    <div style={{ height: '100%', width: st.w, background: 'var(--c-ink)' }} />
                  </div>
                  <span className="num" style={{ width: 96, textAlign: 'right', fontWeight: 600, whiteSpace: 'nowrap' }}>{st.pct}</span>
                </div>
              ))}
            </div>
          </div>
        ) : null}
        </div>
      </div>
      <div style={FRAME}>
        <div />
        {/* Equal columns, not spread edge to edge, so each day number sits
            under its own three bars. */}
        <div style={{
          display: 'grid', gridAutoFlow: 'column', gridAutoColumns: '1fr', fontSize: 10,
          color: 'var(--color-neutral-600)', marginTop: 5, textAlign: 'center',
        }}>
          {c.days.map((d, i) => <span key={i}>{d}</span>)}
        </div>
      </div>
      <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', marginTop: 12 }}>
        {SHIFTS.map((s, si) => (
          <div key={s} style={{
            display: 'flex', alignItems: 'center', gap: 7, fontSize: 12,
            color: 'var(--color-neutral-800)',
          }}>
            <span style={{ width: 12, height: 12, display: 'block', background: DETAIL_COLOR[si] }} />
            Shift {s}
          </div>
        ))}
      </div>
    </>
  )
}

const FRAME: React.CSSProperties = {
  display: 'grid', gridTemplateColumns: '34px minmax(0,1fr)', gap: 8,
}

/** The readout for one shift of one day on a machine's detail chart: its
 *  kilograms against the day's standard, its utilisation — the shift's
 *  minutes less the stoppages the register books on it — who had it, and
 *  those stoppages reason by reason, each as a share of the shift. */
function shiftReadout(m: MachineRow, c: Ctx, hi: number, sh: Shift) {
  const { data } = c
  const n = data.dates.length
  const slot = n ? 1000 / n : 30
  const x = m.days.find((d) => d.date === data.dates[hi])
  const si = SHIFTS.indexOf(sh)
  const xc = (hi * slot + 2 + (si + 0.5) * Math.max(2, (slot - 4) / 3)) / 10
  const kg = x ? x.kg[sh] : null
  const has = kg !== null && kg !== undefined
  const shiftMin = x?.shift_min ?? 480
  const stops = x ? x.shifts[sh].stops : []
  const stopMin = stops.reduce((t, r) => t + r.min, 0)
  const util = has ? Math.max(0, 1 - stopMin / shiftMin) * 100 : null
  return {
    title: `U${m.unit} · ${m.name} · Shift ${sh}`,
    day: `${dayNum(data.dates[hi])} ${c.monShort}`,
    left: xc + '%',
    shift: xc > 80 ? 'translateX(-100%)' : xc < 20 ? 'translateX(0)' : 'translateX(-50%)',
    sub: x
      ? (x.count ? x.count + ' · ' : '') + 'std ' + num(x.std ?? 0) + ' kg/shift · ' + num(shiftMin / 60) + ' h shift'
      : 'No entry for this machine',
    who: x ? 'Supervisor: ' + (x.shifts[sh].supervisor ?? 'not entered') : '',
    figures: [
      { label: 'Prod kg', value: has ? num(kg) : '–' },
      { label: 'Util %', value: util === null ? '–' : num(util, 1) + '%' },
      { label: 'Eff %', value: has && x?.std ? num(kg / x.std * 100, 1) + '%' : '–' },
    ],
    stopHead: stopMin
      ? `Stoppages · ${num(stopMin)} min · ${num(stopMin / shiftMin * 100, 1)}% of the ${num(shiftMin)}-min shift`
      : has ? 'No stoppage booked on this shift' : 'No record for this shift',
    stops: stops.map((r) => ({
      r: r.reason,
      pct: `${num(r.min)} min · ${num(r.min / shiftMin * 100, 1)}%`,
      w: (r.min / stops[0].min * 100) + '%',
    })),
  }
}
